<script setup lang="ts">
import BookingConfirmDialog from "@/components/booking/bookingConfirmDialog.vue";
import GuestBookingStepper from "@/components/booking/guestBookingStepper.vue";
import { useUpdateBlocker } from "@/composables/useDeferredAppUpdate";
import { useBayModeStore } from "@/store/useBayModeStore";
import { useUserModeChange } from "@/store/useUserModeChange";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { usePermission } from "@/utils/usePermission";
import { useUserRole } from "@/utils/useUserRole";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";

// stores
const userStore = useUserStore();
const globalStore = useGlobalSetupStore();
const userRole = useUserRole();
const router = useRouter();
const userMode = useUserModeChange();

const { getHomeAlerts } = globalStore;

const { currentLocationDetails, currentLocation, userId, userInfo } = storeToRefs(userStore);
const { globalSetup, homeAlerts } = storeToRefs(globalStore);
const { rolesList, permission } = storeToRefs(userRole);
const { isUserMode } = storeToRefs(userMode);
const { meetsMinimum } = usePermission();

// Home-screen shortcut visibility, gated on each shortcut's own permission
// (Users/Dockets/reporting need at least View; POS matches its nav threshold of Edit)
const canSeeUsersShortcut = computed(() => permission.value?.users && permission.value.users !== "None");
const canSeeDocketsShortcut = computed(() => permission.value?.dockets && permission.value.dockets !== "None");
const canSeePosShortcut = computed(() => meetsMinimum(permission.value?.dockets, "Edit"));
const canSeeReportsShortcut = computed(() => permission.value?.reporting && permission.value.reporting !== "None");
const showAdminShortcuts = computed(() =>
  canSeeUsersShortcut.value || canSeeDocketsShortcut.value || canSeePosShortcut.value || canSeeReportsShortcut.value
);

const bayModeStore = useBayModeStore();
const { isActive: isBayMode, areaId: bayModeNumber } = storeToRefs(bayModeStore);

// Data
const tooltipText = ref("copy");
const activityList = ref<any[]>([]);
const areaList = ref<any[]>([]);
const initialStep = ref(0);
const showBooking = ref(false);
const activeBooking = ref(false);
const isQuickBooking = ref(false);
const route = useRoute();
const isFromQB = ref(false);
const isBookingConfirmDialogVisible = ref(false);
const editBooking = ref<any>(null);
const selectedActivity = ref<any>(null);
const referrers = ref<any[]>([]);

const shouldBlockAppUpdate = computed(() =>
  showBooking.value || isBookingConfirmDialogVisible.value
);

useUpdateBlocker(
  "quickbook-home-flow",
  shouldBlockAppUpdate,
  "Finish or close quick booking before updating"
);

import { useThemeConfig } from "@core/composable/useThemeConfig";
const systemDarkMode = window.matchMedia("(prefers-color-scheme: dark)");
const { theme } = useThemeConfig();

// Computed
const gethomePageImage = computed(() => {
  if (theme.value === "dark" || (theme.value === "system" && systemDarkMode.matches)) {
    if(globalSetup?.value?.homePageImageDark != '') {
      return `${pb.baseUrl}/api/files/${globalSetup.value.collectionId}/${globalSetup.value.id}/${globalSetup.value.homePageImageDark}`;
    }
  }

  return `${pb.baseUrl}/api/files/${globalSetup.value.collectionId}/${globalSetup.value.id}/${globalSetup.value.homePageImage}`;
});

const activeAlerts = computed(() => {
  return homeAlerts.value?.filter((alert) => {
    const currentTime = moment();

    const startOk = !alert?.startTime || moment(alert.startTime).isSameOrBefore(currentTime);
    const endOk = !alert?.endTime || moment(alert.endTime).isSameOrAfter(currentTime);

    return startOk && endOk;
  });
});

const getReferrers = async () => {
  return await pb.collection("referrers").getFullList();
};

const getActivities = async () => {
  try {
    const filter = !userInfo.value.id ? '&& guestsCanBook = true' : ''
    
    const records = await pb.collection("activities").getFullList({
      expand: "availibleTimes,TODPricing,options,options.category,options.category.TODPricing,options.subCategory,options.subCategory.TODPricing",
      filter: `location = "${currentLocation.value}" ${filter}`,
      sort: "sortOrder",
    });
    if (records) {
      activityList.value = records.filter(record =>
        (userInfo?.value && userInfo?.value?.expand?.membership?.activities?.some((activity: any) => activity === record.id)) ||
        (userInfo.value && record.usersCanBook) ||
        record.guestsCanBook ||
        (userInfo?.value && userInfo.value.roles.length > 0 && !isUserMode.value)
      );
    }
  } catch (e) {}
};

const getArea = async () => {
  const areas: any = await pb.collection("areas").getFullList({ filter: `location = "${currentLocation.value}"` });

  areaList.value = areas;
};

const getPayment = async () => {
  const dpid = route?.query?.dpid;
  if (!dpid) return;
  showBooking.value = true;
};

const handleQuickBooking = (e: any) => {
  e.preventDefault();
  showBooking.value = true;
  isQuickBooking.value = !!userInfo.value?.id && !(userInfo.value.roles?.length > 0 && !isUserMode.value);
};

// Methods
// copy email id to the clipboard
const copyToClipboard = (text: any) => {
  if (!text) return;
  navigator.clipboard
    .writeText(text)
    .then(() => {
      tooltipText.value = "copied";
    })
    .catch((err) => {
      tooltipText.value = "failed to copy";
    })
    .finally(() => {
      setTimeout(() => {
        tooltipText.value = "copy";
      }, 1000);
    });
};
const getMyRole = computed(() => {
  return rolesList?.value?.filter((role: any) =>
    userInfo.value?.roles?.includes(role.id)
  );
});

const subscribeHomeAlerts = async () => {
  try {
    await pb.collection("homeAlerts").subscribe("*", async (res: any) => {
      await getHomeAlerts(currentLocation?.value);
    });
  } catch (e) {
    console.log(e);
  }
};

const handleMemberLogin = () => {
  router.push("auth/login");
};

const handleGuestBooking = () => {
  showBooking.value = true;
};

const handleGuestWebshop = () => {
  router.push("webshop");
};

// watchers to watch user's current location
watch(currentLocation, async (newCurrentLocation) => {
  await getHomeAlerts(newCurrentLocation);
});

// Function to warn user before leaving the page unload
const warnUser = (event: BeforeUnloadEvent) => {
  let onpayment = localStorage.getItem('onpayment')
  if (showBooking.value && !onpayment) {
    event.preventDefault();
    event.returnValue = ""; // Necessary for Chrome to display the dialog
  }
};

// lifecycle hooks
onMounted(async () => {
  try {
    localStorage.removeItem('onpayment')
    await userStore.fetchLoggedInUser()
  }catch(e){}
  const pendingBookingId = localStorage.getItem("pendingBookingId");
  if (pendingBookingId) {
    try {
      const booking = await pb.collection("bookings").getOne(pendingBookingId);
      if (booking.state === "Pending") {
        await pb.collection("bookings").update(pendingBookingId, { 
          state: "Deleted",
          deletedReason: "Navigated away from booking",
        });
      }
    } catch (error) {
      console.error("Error handling pending booking:", error);
    } finally {
      localStorage.removeItem("pendingBookingId");
    }
  }

  window.addEventListener("toggleQuickBooking", (() => {
    if (isFromQB.value) {
      showBooking.value = true;
      isQuickBooking.value = !!userInfo.value?.id && !(userInfo.value.roles?.length > 0 && !isUserMode.value);
    }
    isFromQB.value = true;
  }) as EventListener);

  window.addEventListener("beforeunload", warnUser);

  userId.value = pb.authStore.model?.id;
  await getHomeAlerts(currentLocation.value);
  await subscribeHomeAlerts();
  await getActivities();
  await getArea();
  await getPayment();
  referrers.value = await getReferrers();
  activeBooking.value = true;
  if (route.name == "qb") {
    isFromQB.value = true;
    showBooking.value = true;
    isQuickBooking.value = !!userInfo.value?.id && !(userInfo.value.roles?.length > 0 && !isUserMode.value);
  }
});

const closeQuickBooking = (ev) => {
  showBooking.value = ev;
  try {
    let qbid = localStorage.getItem('qbid')
    if (qbid && qbid != '') {
      localStorage.removeItem('qbid')
    }
  }catch(e){
    console.error(e)
  }
}

const handleShowConfirmDialog = ({ booking, activity }: { booking: any; activity: any }) => {
  editBooking.value = booking;
  selectedActivity.value = activity;
  showBooking.value = false;
  isBookingConfirmDialogVisible.value = true;
};

const handleReferrerUpdate = async (referrer: any) => {
  isBookingConfirmDialogVisible.value = false;
  try {
    if (referrer && editBooking.value?.id) {
      await pb.collection("bookings").update(editBooking.value.id, { referrer });
    }
  } catch(e) {
    console.error('handleReferrerUpdate error', e);
  }
  editBooking.value = null;
  selectedActivity.value = null;
  history.replaceState(null, '', route.path);
};

onUnmounted(() => {
  pb.collection("homeAlerts").unsubscribe("*");
  window.removeEventListener("toggleQuickBooking", ((event: CustomEvent) => {
    showBooking.value = event.detail;
  }) as EventListener);
  window.removeEventListener("beforeunload", warnUser);
});
</script>

<template>
  <div>
    <VSheet class="text-center pa-8 mb-3 rounded-sm">
      <div>
        <img
          v-if="gethomePageImage"
          :src="gethomePageImage"
          alt="Baybooker"
          class="center logoImage"
          style="display: block;min-width: 220px; height: auto; margin: auto;"
        />
      </div>
      <VDivider class="mx-14 my-6" />
      <div class="text-h5">
        Welcome to
        <span class="font-weight-bold">{{ globalSetup.companyName }}</span>
      </div>
      <div v-if="activeAlerts.length" class="mb-2">
        <div
          v-for="alert in activeAlerts"
          :key="alert?.id"
          class="bold_tag my-2"
        >
          <VAlert
            variant="flat"
            :color="alert?.color || 'error'"
            :type="alert?.type?.toLowerCase()"
            :text="alert?.message"
          />
        </div>
      </div>
    </VSheet>

    <!-- Guest buttons section - only show when user is not logged in -->
    <VCard v-if="!pb?.authStore?.model?.id" class="mb-4">
      <VRow justify="center" class="pa-4">
        <VCol cols="12" sm="6" md="4" lg="3">
          <VBtn
            block
            size="x-large"
            class="me-2 custom-size-btn"
            color="secondary"
            @click="handleMemberLogin"
          >
            Existing Members <br />
            Sign in
          </VBtn>
        </VCol>
        <VCol
          v-if="currentLocationDetails.guestBookingsEnabled"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <VBtn
            block
            size="x-large"
            :disabled="!activeBooking"
            color="green"
            class="custom-size-btn"
            @click="handleGuestBooking"
          >
            Quick Book <br />
            as a guest
          </VBtn>
        </VCol>
        <VCol
          v-if="currentLocationDetails.webshopEnabled"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <VBtn
            block
            size="x-large"
            :disabled="!activeBooking"
            color="blue"
            class="custom-size-btn"
            @click="handleGuestWebshop"
          >
            WebShop <br />
            as a guest
          </VBtn>
        </VCol>
      </VRow>
    </VCard>

    <!-- Show 4 Booking of logged in user -->
    <PriorityBookings v-if="pb?.authStore?.model?.id" />

    <VCard class="rounded-sm mt-3" v-if="pb?.authStore?.model?.id">
      <VRow no-gutters>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn
              :to="`/user/${userStore.userId}`"
              block
              class="me-2"
              prepend-icon="mdi-account-outline"
            >
              Profile
            </VBtn>
          </VSheet>
        </VCol>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn
              block
              prepend-icon="mdi-calendar-select"
              @click="handleQuickBooking"
            >
              Quick Book
            </VBtn>
          </VSheet>
        </VCol>
        <VCol>
          <VSheet class="pa-2 ma-2">
            <VBtn to="bookings" block prepend-icon="mdi-calendar-select">
              Bookings
            </VBtn>
          </VSheet>
        </VCol>
      </VRow>

      <VRow
        no-gutters
        v-if="showAdminShortcuts"
      >
        <VCol v-if="canSeeUsersShortcut">
          <VSheet class="pa-2 ma-2">
            <VBtn
              to="/user"
              block
              class="me-2"
              prepend-icon="mdi-account-group-outline"
            >
              Users
            </VBtn>
          </VSheet>
        </VCol>
        <VCol v-if="canSeeDocketsShortcut">
          <VSheet class="pa-2 ma-2">
            <VBtn block to="dockets" class="me-2" prepend-icon="mdi-cart">
              Dockets
            </VBtn>
          </VSheet>
        </VCol>
        <VCol v-if="canSeePosShortcut">
          <VSheet class="pa-2 ma-2">
            <VBtn to="/pos" block class="me-2" prepend-icon="mdi-network-pos">
              POS
            </VBtn>
          </VSheet>
        </VCol>
        <VCol v-if="canSeeReportsShortcut">
          <VSheet class="pa-2 ma-2">
            <VBtn to="reports" block prepend-icon="mdi-chart-bar">
              Reports
            </VBtn>
          </VSheet>
        </VCol>
      </VRow>
    </VCard>

    <VCard class="mt-6 px-8 py-4 rounded-sm">
      <VRow no-gutters>
        <VCol>
          <VSheet class="text-left">
            <VCardTitle class="ps-4">
              {{ globalSetup.companyName }}
            </VCardTitle>
            <VCardText class="py-0 ps-4">
              <div>
                <p>{{ currentLocationDetails.address }}</p>
                <p>{{ currentLocationDetails.suburb }}</p>
                <p>
                  {{ currentLocationDetails.city }}
                  {{ currentLocationDetails.postcode }}
                </p>
                <p>{{ currentLocationDetails.country }}</p>
                <p class="pt-1">
                  <a
                    :href="`https://maps.google.com/?q=${currentLocationDetails.address},${currentLocationDetails.suburb},${currentLocationDetails.city} ${currentLocationDetails.postcode},${currentLocationDetails.country}`"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Google Maps
                    <VIcon icon="mdi-map-marker" size="small" />
                  </a>
                </p>
              </div>
            </VCardText>
          </VSheet>
        </VCol>
        <VCol />

        <VCol>
          <VSheet class="text-left">
            <VCardTitle class="ps-4"> Contact details </VCardTitle>
            <VCardText class="d-flex flex-column align-start ps-4">
              <VBtn v-if="currentLocationDetails?.phone && currentLocationDetails.phone != ''"
                prepend-icon="mdi-phone"
                variant="plain"
                class="px-0"
                @click="copyToClipboard(currentLocationDetails.phone)"
              >
                {{ currentLocationDetails.phone || "phone" }}
                <VTooltip activator="parent" location="right">
                  {{ tooltipText }}
                </VTooltip>
              </VBtn>
              <VBtn
                prepend-icon="mdi-email"
                variant="plain"
                class="px-0"
                @click="copyToClipboard(currentLocationDetails.email)"
              >
                {{ currentLocationDetails.email || "email" }}
                <VTooltip activator="parent" location="right">
                  {{ tooltipText }}
                </VTooltip>
              </VBtn>
              <VBtn
                :href="currentLocationDetails.website"
                prepend-icon="mdi-globe"
                variant="plain"
                class="px-0 website_url"
                target="_blank"
              >
                <div>
                  {{ currentLocationDetails.website || "website" }}
                </div>
              </VBtn>
              <VBtn
                v-if="currentLocationDetails?.facebookLink && currentLocationDetails.facebookLink != ''"
                :href="currentLocationDetails.facebookLink"
                prepend-icon="mdi-facebook"
                variant="plain"
                class="px-0"
                target="_blank"
                rel="noopener noreferrer"
              >
                Facebook
              </VBtn>
              <VBtn
                v-if="currentLocationDetails?.instagramLink && currentLocationDetails.instagramLink != ''"
                :href="currentLocationDetails.instagramLink"
                prepend-icon="mdi-instagram"
                variant="plain"
                class="px-0"
                target="_blank"
                rel="noopener noreferrer"
              >
                Instagram
              </VBtn>
            </VCardText>
          </VSheet>
        </VCol>
        <VCol />
      </VRow>
    </VCard>
    <GuestBookingStepper
      v-if="showBooking"
      v-model:is-drawer-open="showBooking"
      :location="currentLocation"
      :time-zone="currentLocationDetails.timeZoneCode"
      :activity-list="activityList"
      :area-list="areaList"
      :current-logged-in-roles="getMyRole"
      :user-id="userId ? userId : ''"
      :membership="!isUserMode ? userInfo?.membership : null"
      :is-quick-booking="isQuickBooking"
      :logged-user="userInfo"
      :initial-step="initialStep"
      :default-area="isBayMode ? bayModeNumber : null"
      @closeDialog="closeQuickBooking($event)"
      @showConfirmDialog="handleShowConfirmDialog"
    />
    <BookingConfirmDialog
      v-if="editBooking"
      v-model="isBookingConfirmDialogVisible"
      :booking="editBooking"
      :activities="selectedActivity"
      :referrers="referrers"
      @closeDialog="handleReferrerUpdate"
    />
  </div>
</template>

<style scoped lang="scss">
p {
  margin: 0;
}

.logoImage {
  block-size: 294px;
  inline-size: 400px;
  object-fit: contain;

  @media (max-width: 600px) {
    block-size: auto;
    inline-size: 100%;
  }
}

.custom-size-btn {
  block-size: 124px;
}

@media (max-width: 390px) {
  .website_url {
    margin-inline-start: 10px;
  }
}
</style>
